import { parseISO } from "date-fns";
import { getLiveWindowStart } from "./constants";
import { computeCumulativeHours, resolveCumulativeFromDate, sumExtraHoursPaidWithinWindow } from "./calculations";
import { getCurrentRunStartDate } from "./employmentRun";
import { WorkLog, TimeOff, StaffWeeklyHours } from "../types";

// A frozen yearly balance carried forward from previous years (see IMPLEMENTATION_PLAN.md §4a).
// computed_* are auto-derived; manual_balance is an optional admin override.
// Decimal columns can arrive from the Directus API as strings, hence number | string.
export type StaffHoursSnapshot = {
    id?: number;
    user?: string;
    checkpoint_date: string;
    computed_worked?: number | string;
    computed_expected?: number | string;
    computed_balance: number | string;
    manual_balance?: number | string | null;
    note?: string;
};

export const getLatestSnapshot = (snapshots: Array<StaffHoursSnapshot>): StaffHoursSnapshot | null => {
    if (!snapshots || snapshots.length === 0) {
        return null;
    }
    const latest = snapshots.reduce((selected, current) =>
        current.checkpoint_date > selected.checkpoint_date ? current : selected
    );
    return latest;
};

// The carried-forward balance: an admin manual override wins over the auto-computed value.
// Coerces to a number because Directus may return decimal columns as strings.
export const getEffectivePriorBalance = (snapshot: StaffHoursSnapshot | null): number => {
    if (!snapshot) {
        return 0;
    }
    const rawBalance = snapshot.manual_balance ?? snapshot.computed_balance ?? 0;
    const balance = Number(rawBalance);
    return Number.isFinite(balance) ? balance : 0;
};

// Turns a user's snapshot rows into the live-window start and the prior balance to carry in.
// No snapshot → hard floor + 0; with a snapshot → latest checkpoint + 1 day + the effective balance.
// A current run starting AFTER the latest checkpoint means the old relationship was settled at the
// switch: the snapshot belongs to it, so the new relationship starts a fresh bag with balance 0.
export const resolveUserSnapshotContext = (
    snapshots: Array<StaffHoursSnapshot>,
    currentRunStartDate: Date | null = null,
) => {
    const latest = getLatestSnapshot(snapshots);
    const snapshotBelongsToPreviousRun =
        latest !== null && currentRunStartDate !== null && currentRunStartDate > parseISO(latest.checkpoint_date);
    if (snapshotBelongsToPreviousRun) {
        const freshContext = { windowStart: getLiveWindowStart(null), priorBalance: 0 };
        return freshContext;
    }
    const windowStart = getLiveWindowStart(latest ? parseISO(latest.checkpoint_date) : null);
    const priorBalance = getEffectivePriorBalance(latest);
    return { windowStart, priorBalance };
};

type ResolveRunBagWindowInput = {
    weeklyHoursSchedule: Array<StaffWeeklyHours>;
    snapshots: Array<StaffHoursSnapshot>;
    extraHoursPayments: Array<any>;
    referenceDate: Date;
};

// The bag window of the run in force on referenceDate: where it starts, the balance carried into it and the extra
// hours already paid out inside it.
export const resolveRunBagWindow = ({ weeklyHoursSchedule, snapshots, extraHoursPayments, referenceDate }: ResolveRunBagWindowInput) => {
    const runStartDate = getCurrentRunStartDate(weeklyHoursSchedule, referenceDate);
    const { windowStart, priorBalance } = resolveUserSnapshotContext(snapshots, runStartDate);
    const cumulativeFromDate = resolveCumulativeFromDate(windowStart, runStartDate);
    const extraHoursPaid = sumExtraHoursPaidWithinWindow(extraHoursPayments, cumulativeFromDate);
    const bagWindow = { runStartDate, windowStart, priorBalance, cumulativeFromDate, extraHoursPaid };
    return bagWindow;
};

type BuildSnapshotPayloadInput = {
    user: string;
    checkpointDate: string;
    worked: number;
    expected: number;
    priorBalance: number;
};

// computed_worked/expected = the year's increment; computed_balance = lifetime carry-forward.
export const buildSnapshotPayload = ({ user, checkpointDate, worked, expected, priorBalance }: BuildSnapshotPayloadInput) => {
    const payload = {
        user,
        checkpoint_date: checkpointDate,
        computed_worked: worked,
        computed_expected: expected,
        computed_balance: priorBalance + (worked - expected),
    };
    return payload;
};

// The 31-Dec checkpoint to freeze, or null. Freezable only when today is past the 31-Jan grace for that
// year, the whole year is after the hard-floor cutoff (first freezable = 2026), and it isn't frozen yet.
export const getYearToFreeze = (now: Date, existingSnapshots: Array<StaffHoursSnapshot>) => {
    const isPastJanuaryGrace = now.getMonth() > 0 || now.getDate() >= 31;
    const candidateYear = isPastJanuaryGrace ? now.getFullYear() - 1 : now.getFullYear() - 2;

    const hardFloor = getLiveWindowStart(null);
    const candidateYearStart = new Date(candidateYear, 0, 1);
    if (candidateYearStart < hardFloor) {
        return null;
    }

    const alreadyFrozen = existingSnapshots.some(
        (snapshot) => parseISO(snapshot.checkpoint_date).getFullYear() === candidateYear
    );
    if (alreadyFrozen) {
        return null;
    }

    const checkpoint = new Date(candidateYear, 11, 31);
    return checkpoint;
};

// True when a recomputed closed-period balance has drifted from the stored snapshot beyond a tolerance
// (e.g. someone edited an already-frozen schedule/time-off). Coerces in case of string decimals.
export const detectSnapshotDrift = (storedBalance: number | string, recomputedBalance: number, tolerance: number) => {
    const drift = Math.abs(Number(storedBalance) - recomputedBalance) > tolerance;
    return drift;
};

type ComputeEmployeeSnapshotPayloadInput = {
    user: string;
    checkpoint: Date;
    workLogs: Array<WorkLog>;
    timeOffs: Array<TimeOff>;
    weeklyWorkHoursSchedule: Array<StaffWeeklyHours>;
    publicHolidaysSet: Set<string>;
    expectedHoursCorrection: number;
    extraHoursPaid: number;
    employmentStartDate: Date | null;
    currentRunStartDate?: Date | null;
    existingSnapshots: Array<StaffHoursSnapshot>;
};

// One employee's freeze payload up to `checkpoint`, carrying their existing snapshot balance forward.
export const computeEmployeeSnapshotPayload = ({
    user,
    checkpoint,
    workLogs,
    timeOffs,
    weeklyWorkHoursSchedule,
    publicHolidaysSet,
    expectedHoursCorrection,
    extraHoursPaid,
    employmentStartDate,
    currentRunStartDate = null,
    existingSnapshots,
}: ComputeEmployeeSnapshotPayloadInput) => {
    const { windowStart, priorBalance } = resolveUserSnapshotContext(existingSnapshots, currentRunStartDate);
    const { cumulativeWorked, cumulativeExpected } = computeCumulativeHours({
        workLogs,
        timeOffs,
        toDate: checkpoint,
        weeklyWorkHoursSchedule,
        publicHolidaysSet,
        expectedHoursCorrection,
        extraHoursPaid,
        cumulativeStartDate: windowStart,
        employmentStartDate,
        priorBalance,
    });
    const payload = buildSnapshotPayload({
        user,
        checkpointDate: checkpoint.toISOString(),
        worked: cumulativeWorked,
        expected: cumulativeExpected,
        priorBalance,
    });
    return payload;
};
